<script setup>
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const relationship = ref(null);
const recurrence = ref([]);
const isLoading = ref(true);

function formatDate(value) {
  if (!value) return 'Nenhum';
  return new Intl.DateTimeFormat('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

function nextAppointmentLabel(appointment) {
  if (!appointment) return 'Nenhum';
  return `${formatDate(`${appointment.scheduled_date}T${appointment.scheduled_time}`)} as ${appointment.scheduled_time.slice(0, 5)}`;
}

const lastVisitLabel = computed(() => {
  if (!relationship.value?.lastVisitAt) return 'Nenhum registrado';
  const days = relationship.value.daysSinceLastVisit;
  return `${formatDate(relationship.value.lastVisitAt)} (${days} ${days === 1 ? 'dia' : 'dias'})`;
});

async function fetchRelationship() {
  isLoading.value = true;
  try {
    const response = await fetch(
      `/api/superwoot/contact-service-history?contactId=${encodeURIComponent(props.contactId)}&pageSize=1`
    );
    if (!response.ok) throw new Error('request_failed');
    const payload = await response.json();
    relationship.value = payload.relationship;
    recurrence.value = payload.recurrence || [];
  } catch {
    relationship.value = null;
    recurrence.value = [];
  } finally {
    isLoading.value = false;
  }
}

watch(() => props.contactId, fetchRelationship);
onMounted(fetchRelationship);
</script>

<template>
  <section class="w-full pt-6 border-t border-n-strong">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-heart-handshake size-4 text-n-slate-10" />
      <h4 class="text-sm font-medium text-n-slate-12">Relacionamento</h4>
    </div>
    <div v-if="isLoading" class="text-sm text-n-slate-10">
      Carregando relacionamento...
    </div>
    <div v-else class="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div class="p-3 border rounded-md border-n-weak bg-n-solid-2">
        <span class="block text-xs text-n-slate-10">Ultimo atendimento</span>
        <strong class="block mt-1 text-sm font-medium text-n-slate-12">
          {{ lastVisitLabel }}
        </strong>
      </div>
      <div class="p-3 border rounded-md border-n-weak bg-n-solid-2">
        <span class="block text-xs text-n-slate-10">Proximo agendamento</span>
        <strong class="block mt-1 text-sm font-medium text-n-slate-12">
          {{ nextAppointmentLabel(relationship?.nextAppointment) }}
        </strong>
      </div>
      <div class="p-3 border rounded-md border-n-weak bg-n-solid-2">
        <span class="block text-xs text-n-slate-10">Atendimentos concluidos</span>
        <strong class="block mt-1 text-sm font-medium text-n-slate-12">
          {{ relationship?.completedVisits || 0 }}
        </strong>
      </div>
      <div class="p-3 border rounded-md border-n-weak bg-n-solid-2">
        <span class="block text-xs text-n-slate-10">Servicos acompanhados</span>
        <strong class="block mt-1 text-sm font-medium text-n-slate-12">
          {{ recurrence.length }}
        </strong>
      </div>
      <div class="p-3 border rounded-md border-n-weak bg-n-solid-2">
        <span class="block text-xs text-n-slate-10">Faltas</span>
        <strong class="block mt-1 text-sm font-medium text-n-slate-12">
          {{ relationship?.appointmentMetrics?.noShows || 0 }}
        </strong>
      </div>
      <div class="p-3 border rounded-md border-n-weak bg-n-solid-2">
        <span class="block text-xs text-n-slate-10">Cancelamentos</span>
        <strong class="block mt-1 text-sm font-medium text-n-slate-12">
          {{ relationship?.appointmentMetrics?.cancelled || 0 }}
        </strong>
      </div>
    </div>
  </section>
</template>
